import { format } from "date-fns";

/** The number of milliseconds in 1 second */
export const MS_IN_S = 1000;
/** The number of seconds in 1 minute */
export const S_IN_M = 60;
/** The number of minutes in 1 hour */
export const M_IN_H = 60;
/** The number of hours in 1 day */
export const H_IN_D = 24;
/** The number of days in 1 year */
export const D_IN_Y = 365;

/**
 * Get the full year of an input date, defaulting to the current year
 *
 * @param date - A date to grab the year from — Default: `new Date()`
 * @returns The year of the input date (or the current year)
 */
export function getYear(date: Date | number | string = new Date()): number {
  return new Date(date).getFullYear();
}

/**
 * A string representing a date formatted in ISO8601
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date#value}
 *
 * @param date - A date to grab the year from — Default: `new Date()`
 * @returns A date formatted an ISO8601 string
 */
export function formatDateForInput(
  date: Date | number | string = new Date()
): string {
  return format(new Date(date), "yyyy-MM-dd");
}

/**
 * Format a date from a date object
 *
 * @param date - A date to grab the timestamp from — Default: `new Date()`
 * @returns A date formatted as M/D/YYYY
 */
export function formatDate(date: Date): string {
  return format(date, "M/d/yyyy");
}

/**
 * Parse a date into the `timestamp` format used by the PrivCo backend.  This uses seconds instead of milliseconds
 *
 * @param date - A date to grab the timestamp from — Default: `new Date()`
 * @returns A date formatted in
 */
export function getServerTimestamp(date: Date | string = new Date()): number {
  return Math.round(new Date(date).getTime() / MS_IN_S);
}

/**
 * Parse a `timestamp` format used by the PrivCo backend into a Date object
 *
 * @param timestamp - A date to grab the timestamp from — Default: `new Date()`
 * @returns A date formatted as M/D/YYYY
 */
export function parseServerTimestamp(timestamp: number | string): Date {
  return new Date(Number(timestamp) * MS_IN_S);
}

/**
 * Parse a `timestamp` format used by the PrivCo backend into a readable date string.
 *
 * @param timestamp - A date to grab the timestamp from — Default: `new Date()`
 * @returns A date formatted as M/D/YYYY
 */
export function formatServerTimestamp(timestamp: number | string): string {
  return format(parseServerTimestamp(timestamp), "M/d/yyyy");
}

/**
 * Get a date string to use in filenames in the format `YYYY-MM-DD` or `YYYY-MM-DD_HH-MM-SS`
 *
 * @param date - An optional date to use for getting a date string
 * @param includeTime - If passed, the returned date string will include the time
 * @returns A date string in the format `YYYY-MM-DD`
 */
export function getFilenameDate(
  date = new Date(),
  includeTime = false
): string {
  let dateFormat = "yyyy-MM-dd";

  if (includeTime) {
    dateFormat += "_hh-mm-ss";
  }

  return format(date, dateFormat);
}

export const newUtcDate = (input: string) => {
  const utcDate = new Date(input);
  utcDate.setTime(utcDate.getTime() + utcDate.getTimezoneOffset() * 60 * 1000);
  return utcDate;
};
